import { Label } from "@/components/ui/label";
import {
  Select,
  SelectContent,
  SelectGroup,
  SelectItem,
  SelectLabel,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  useLineItemCustomFieldNames,
  useQuoteCustomFieldNames,
} from "@/store/quoteSettingsStore";
import { LINE_FIELD_OPTIONS, type LineFieldOption } from "./lineFields";
import { QUOTE_FIELD_OPTIONS } from "./quoteFields";

/** Sentinel Select value standing for "no bound field" (Radix forbids ""). */
const UNBOUND_VALUE = "__unbound";

/** Which catalog of sources a picker offers: quote-level or per-line. */
export type FieldSourceScope = "quote" | "line";

/** The per-scope catalog, group heading, and control label. */
const SCOPE_CATALOGS: Record<
  FieldSourceScope,
  { options: readonly LineFieldOption[]; groupLabel: string; label: string }
> = {
  quote: {
    options: QUOTE_FIELD_OPTIONS,
    groupLabel: "Quote fields",
    label: "Field source",
  },
  line: {
    options: LINE_FIELD_OPTIONS,
    groupLabel: "Line fields",
    label: "Line value",
  },
};

interface FieldSourceSelectProps {
  /** The block's current `source`, or null when it binds nothing. */
  value: string | null;
  /** Which catalog to offer: quote-level fields or per-line fields. */
  scope: FieldSourceScope;
  /** Offers an "unbind" option, for a block that can hold authored text instead. */
  clearable: boolean;
  /** Writes (or clears, on "") the `source` attribute on the selected block. */
  onChange: (source: string) => void;
}

/**
 * The field picker for a block's `source`. It is the bespoke control the prop
 * schema's override map points `source` at: the org's custom fields are loaded
 * at runtime, so the generated select cannot enumerate them.
 * @param value - The block's current source
 * @param scope - Which catalog to offer
 * @param clearable - Whether the block may bind nothing
 * @param onChange - Writes or clears the source
 * @returns The field source picker
 */
export function FieldSourceSelect({
  value,
  scope,
  clearable,
  onChange,
}: FieldSourceSelectProps) {
  const quoteCustomFieldNames = useQuoteCustomFieldNames();
  const lineCustomFieldNames = useLineItemCustomFieldNames();
  const customFieldNames =
    scope === "line" ? lineCustomFieldNames : quoteCustomFieldNames;
  const catalog = SCOPE_CATALOGS[scope];
  return (
    <div>
      <Label className="mb-1 block text-[11px]">{catalog.label}</Label>
      <Select
        value={value ?? (clearable ? UNBOUND_VALUE : undefined)}
        onValueChange={(next) => onChange(next === UNBOUND_VALUE ? "" : next)}
      >
        <SelectTrigger className="h-8 text-xs">
          <SelectValue placeholder="Pick a field" />
        </SelectTrigger>
        <SelectContent>
          {clearable && (
            <SelectItem value={UNBOUND_VALUE}>
              None (use the text written here)
            </SelectItem>
          )}
          {customFieldNames.length > 0 && (
            <SelectGroup>
              <SelectLabel>Custom fields</SelectLabel>
              {customFieldNames.map((name) => (
                <SelectItem key={`custom:${name}`} value={`custom:${name}`}>
                  {name}
                </SelectItem>
              ))}
            </SelectGroup>
          )}
          <SelectGroup>
            <SelectLabel>{catalog.groupLabel}</SelectLabel>
            {catalog.options.map((option) => (
              <SelectItem key={option.source} value={option.source}>
                {option.label}
              </SelectItem>
            ))}
          </SelectGroup>
        </SelectContent>
      </Select>
    </div>
  );
}
